import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { Box, Typography } from '@material-ui/core';
import { AppointmentCountRecurrent, status as AppointmentStatus } from '@spike/appointment-model';
import { AppointmentOrderItem, CustomOrderItem, Order, OrderItem, OrderizableTypes } from '@spike/order-model';
import Invoice from '@spike/invoice-model';
import Pet from '@spike/pet-model';
import { EvaluationReport } from '@spike/report-model';
import clsx from 'clsx';
import PetParent from 'components/Checkout2/PetParent';
import { Title } from 'components/Checkout2/UI/Title';
import { AlertNewBooking } from 'components/CreateBooking/UI/AlertNewBooking';
import Notes from 'components/OrderDetail/Notes';
import PetDetail from 'components/OrderDetail/PetDetail';
import RequestedDate from 'components/OrderDetail/RequestDate';
import BookingRecurrencyInfo from 'components/OrderDetail/UI/BookingRecurrencyInfo';
import { ExtendRecurringSeries } from 'components/OrderDetail/ExtendRecurringSeries';
import { Button, Spinner } from 'components/UI';
import { Switch } from 'components/UI/V3/Forms/Switch';
import V3Spinner from 'components/UI/V3/Spinner';
import { FunctionComponent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import LatePickUpFeeToggle from '../../UI/LatePickUpFeeToggle/LatePickUpFeeToggle';
import { useApiClientWrapper, useMarketplace, usePermissions, useTimeZone } from 'hooks';
import useNonInitialEffect from '@versiondos/hooks';
import { useDispatch, useSelector } from 'react-redux';
import {
    addOrderItemsScopedThunk,
    deleteOrderItemThunk,
    INITIAL_ORDER_EDIT_SCOPE_REQUEST,
    OrdersStatus,
    removeOrderItemsScopedThunk,
    saveOrderItemThunk,
    updateOrderItemScopedThunk
} from '@spike/orders-action';
import { isOrderInRecurringSeries } from '../../isOrderInRecurringSeries';
import moment from 'moment-timezone';
import { RootState } from 'store';
import { Vehicle } from '@spike/vehicle-model';
import { fetchThunk } from '@spike/vehicles-action';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import styles from './OrderDetailDetailsTab.module.scss';
import { RoomsState, searchThunk } from '@spike/rooms-action';
import { MarketplaceDepositType } from '@spike/marketplace-model';
import UnsignedAgreementAlert from 'components/OrderDetail/UnsignedAgreements';
import { getOrderThunk } from '@spike/orders-action';
import {
    buildLateCheckoutFeeOrderItem,
    calculateAutomaticLateCheckoutFee,
    calculateLateCheckoutFee,
    findLateCheckoutFeeCustomItem,
    LATE_CHECKOUT_FEE_DESCRIPTION,
    lateCheckoutAmountsMatch
} from 'utils/lateCheckoutFee';

interface OrderDetailDetailsTabProps {
    // Loading states
    loading: {
        order: boolean;
        services: boolean;
        pets: boolean;
        recurrent: boolean;
        extend: boolean;
    };

    // Order data
    order?: Order;
    invoice?: Invoice;
    sortedPets: Pet[];
    orderItemsByPet: Map<number, any>;
    notes: string;
    recurrentInfo?: AppointmentCountRecurrent;
    isLastRecurrentAppointment: boolean;
    canExtendSeries: boolean;
    seriesExtending: boolean;

    // Flags
    isRequestedOrder: boolean;
    isDeclinedOrder: boolean;
    isOrderClosed: boolean | undefined;
    canAddItemsAndPets: boolean;
    canEditAppointmentItem: (appointmentId: number, statusId: number) => boolean;
    hasOnlyOnePet: boolean;
    hasOnlyOneService: boolean;
    showAddPetButton: boolean;
    isEvaluation: boolean;

    // State
    edit: { order: boolean; orderItemId: number | undefined };
    savingStatusAppointmentId?: number;
    resultsDrawerZIndex?: number;

    // Event handlers
    setShowClientView: (clientView: { clientId: number | undefined }) => void;
    setShowTwoWays: (show: boolean) => void;
    goToCalendaryDayHandler: (order: Order) => void;
    extend: () => void;
    extendSeries: () => Promise<boolean>;
    editOrderItemHandler: (orderItemId: number) => void;
    addOrderItemHandler: (petId: number, orderItemType: OrderizableTypes) => void;
    confirmDeleteHanlder: (orderItemId: number) => void;
    viewReportHandler: (appointmentId: number, businessAreaId: string) => void;
    addReportHandler: (appointmentId: number, businessAreaId: string) => void;
    confirmDeletePetHandler: (pet: Pet) => void;
    setSavingStatusAppointmentId: (appointmentId: number | undefined) => void;
    setServiceToRebook: (appointmentItem: any) => void;
    setShowPetCard: (petCard: { petId: number | undefined; optionSelected: string | undefined }) => void;
    setAddPet: (add: boolean) => void;
    setNotes: (notes: string) => void;
    onAddEvaluation?: (petId: number, orderItemType: OrderizableTypes) => void;
    onViewResults?: (evaluationReport?: EvaluationReport) => void;
    classNames: Record<string, string>;
    readOnly?: boolean;
    onOpenCollectDeposit?: () => void;
    depositPendingResolved?: boolean;
    depositToggleLoading?: boolean;
    onToggleDeposit?: (enabled: boolean) => void;
}

const cancelledStatuses = [AppointmentStatus.CANCELLED, AppointmentStatus.NO_SHOW, AppointmentStatus.DECLINED];

const closedAppointmentStatuses = [AppointmentStatus.COMPLETED];

const isManualLateCheckoutAmount = (amount: number, automaticAmount: number): boolean => {
    if (automaticAmount <= 0) return amount > 0;
    return !lateCheckoutAmountsMatch(amount, automaticAmount);
};

export const OrderDetailDetailsTab: FunctionComponent<OrderDetailDetailsTabProps> = props => {
    const apiClientWrapper = useApiClientWrapper();
    const dispatch = useDispatch();
    const timezone = useTimeZone();
    const marketplace = useMarketplace();

    const showDepositPendingBanner =
        !!props.order &&
        Number(props.invoice?.deposits?.unpaidTotal ?? 0) > 0 &&
        !props.depositPendingResolved &&
        marketplace.deposit?.type !== MarketplaceDepositType.None &&
        !props.isRequestedOrder &&
        !props.isDeclinedOrder &&
        props.isOrderClosed !== true;
    const hasDeposit = (props.invoice?.deposits?.list?.length ?? 0) > 0;
    const hasUnpaidDeposit = props.invoice?.deposits?.list?.some(deposit => !deposit.paid) ?? false;
    const showDepositToggle =
        !!props.order &&
        marketplace.deposit?.type !== MarketplaceDepositType.None &&
        !props.isRequestedOrder &&
        !props.isDeclinedOrder &&
        props.isOrderClosed !== true;

    const permissions = usePermissions();
    const reopenClosedBookings = permissions.checkout.reopenClosedBookings;
    const unsignedAgreements =
        props.order?.unsignedAgreements.filter(agreement => Boolean(agreement.signatureId)) ?? [];

    const hideAddActionsForClosedAppointments = useMemo(() => {
        if (reopenClosedBookings) return false;
        const appointments = props.order?.items.appointments ?? [];
        if (appointments.length === 0) return false;
        return appointments.every(item => closedAppointmentStatuses.includes(item.appointment.status.id));
    }, [props.order, reopenClosedBookings]);

    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);
    const vehicles = useSelector<RootState, Vehicle[]>(state => state.vehicles.vehicles);
    const rooms = useSelector<RootState, any>(state => state.rooms?.rooms);

    const initialLateCheckoutFee = findLateCheckoutFeeCustomItem(props.order);
    const initialAutomaticLateCheckoutFee = props.order
        ? calculateAutomaticLateCheckoutFee(props.order.items.appointments ?? [], timezone)
        : 0;
    const [customLatePickUpFee, setCustomLatePickUpFee] = useState<CustomOrderItem>(
        initialLateCheckoutFee ?? {
            orderItem: {
                id: undefined,
                uuid: apiClientWrapper.uuid(),
                orderId: props.order?.id,
                orderizable: {
                    type: OrderizableTypes.CUSTOM
                },
                petId: props.order?.items.appointments[0].appointment?.pet?.id,
                staffId: undefined,
                description: LATE_CHECKOUT_FEE_DESCRIPTION,
                quantity: 1,
                amount: '0',
                discount: { amount: '0' },
                startedAt: moment.tz(timezone)
            }
        }
    );
    const [latePickupChecked, setLatePickupChecked] = useState<boolean>(
        initialLateCheckoutFee?.orderItem.id != null &&
            isManualLateCheckoutAmount(Number(initialLateCheckoutFee.orderItem.amount), initialAutomaticLateCheckoutFee)
    );
    const [firstLoad, setFirstLoad] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const lastSavedValueRef = useRef<number | undefined>(undefined);
    const lastDispatchedValueRef = useRef<number | undefined>(undefined);
    const latePickUpFeeScopedSaveRef = useRef<boolean>(false);
    const skipLatePickupCheckedEffectRef = useRef<boolean>(false);
    const userSetSwitchRef = useRef(false);
    const autoAppliedKeyRef = useRef<string | null>(null);

    const { getOrganizationMarketplace, organizationMarketplaces } = useOrganizationMarketplaces();
    const isReadOnly = props.readOnly ?? false;
    const depositToggleDisabled = isReadOnly || props.depositToggleLoading || (hasDeposit && !hasUnpaidDeposit);
    const petHasActiveService = (petId?: number) =>
        (props.order?.items.appointments ?? []).some(
            item => item.pet.id === petId && !cancelledStatuses.includes(item.appointment.status.id)
        );

    useEffect(() => {
        if (!props.order) return;

        const existingCustom = findLateCheckoutFeeCustomItem(props.order);

        if (existingCustom) {
            setCustomLatePickUpFee(existingCustom);
            lastSavedValueRef.current = Number(existingCustom.orderItem.amount);
            if (!userSetSwitchRef.current) {
                const automaticAmount = calculateAutomaticLateCheckoutFee(
                    props.order.items.appointments ?? [],
                    timezone
                );
                setLatePickupChecked(
                    isManualLateCheckoutAmount(Number(existingCustom.orderItem.amount), automaticAmount)
                );
            }
        } else {
            if (!userSetSwitchRef.current) {
                setLatePickupChecked(false);
            }
            setCustomLatePickUpFee(prev => ({
                ...prev,
                orderItem: {
                    ...prev.orderItem,
                    orderId: props.order?.id
                }
            }));
        }
        if (vehicles?.length === 0) {
            dispatch(fetchThunk(apiClientWrapper));
        }
        if (rooms?.rooms === 0) {
            dispatch(fetchRooms());
        }

        setTimeout(() => {
            setFirstLoad(false);
        }, 2000);
    }, [props.order]);

    const getLatePickUpFeeDate = (): moment.Moment => {
        const pickUp = (props.order?.items.appointments ?? [])
            .map(item => item.appointment.duration.to)
            .reduce<moment.Moment | undefined>(
                (latest, to) => (latest === undefined || latest.isBefore(to) ? to.clone() : latest),
                undefined
            );

        return pickUp ?? props.order?.issuedAt?.clone() ?? moment.tz(timezone);
    };

    const saveLatePickUpFee = (latePickUpFee: OrderItem) => {
        const orderItem: OrderItem = { ...latePickUpFee, startedAt: getLatePickUpFeeDate() };

        if (props.order && isOrderInRecurringSeries(props.order)) {
            latePickUpFeeScopedSaveRef.current = true;
            if (orderItem.id === undefined) {
                dispatch(
                    addOrderItemsScopedThunk(
                        apiClientWrapper,
                        props.order.id,
                        [orderItem],
                        INITIAL_ORDER_EDIT_SCOPE_REQUEST
                    )
                );
            } else {
                dispatch(updateOrderItemScopedThunk(apiClientWrapper, orderItem, INITIAL_ORDER_EDIT_SCOPE_REQUEST));
            }
        } else {
            dispatch(saveOrderItemThunk(apiClientWrapper, orderItem));
        }
    };

    const removeLatePickUpFee = () => {
        const orderItemId = customLatePickUpFee.orderItem.id;

        if (!props.order || orderItemId === undefined) {
            return;
        }

        if (isOrderInRecurringSeries(props.order)) {
            latePickUpFeeScopedSaveRef.current = true;
            dispatch(
                removeOrderItemsScopedThunk(
                    apiClientWrapper,
                    props.order.id,
                    [orderItemId],
                    INITIAL_ORDER_EDIT_SCOPE_REQUEST
                )
            );
        } else {
            dispatch(deleteOrderItemThunk(apiClientWrapper, props.order.id, orderItemId));
        }
    };

    const resetLatePickUpFeeToSavedOrder = () => {
        const savedCustom = findLateCheckoutFeeCustomItem(props.order);
        const automaticAmount = props.order
            ? calculateAutomaticLateCheckoutFee(props.order.items.appointments ?? [], timezone)
            : 0;
        const savedChecked =
            savedCustom != null && isManualLateCheckoutAmount(Number(savedCustom.orderItem.amount), automaticAmount);

        if (savedCustom) {
            setCustomLatePickUpFee(savedCustom);
            lastSavedValueRef.current = Number(savedCustom.orderItem.amount);
        } else {
            setCustomLatePickUpFee(prev => ({ ...prev, orderItem: { ...prev.orderItem, id: undefined } }));
            lastSavedValueRef.current = undefined;
        }
        lastDispatchedValueRef.current = undefined;

        if (latePickupChecked !== savedChecked) {
            skipLatePickupCheckedEffectRef.current = true;
            setLatePickupChecked(savedChecked);
        }
    };

    const handleSetCurrentValue = (value?: number) => {
        if (isReadOnly) return;
        userSetSwitchRef.current = true;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            const existingCustom = findLateCheckoutFeeCustomItem(props.order);
            if (value === lastSavedValueRef.current || value === lastDispatchedValueRef.current) {
                return;
            }
            if (!existingCustom || (existingCustom && Number(existingCustom.orderItem.amount) !== value)) {
                const custom = { ...customLatePickUpFee };
                custom.orderItem.amount = value?.toString() || '0';
                setCustomLatePickUpFee(custom);
                lastDispatchedValueRef.current = value;
                saveLatePickUpFee(custom.orderItem);
            }
        }, 2000);
    };

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    useNonInitialEffect(() => {
        if (skipLatePickupCheckedEffectRef.current) {
            skipLatePickupCheckedEffectRef.current = false;
            return;
        }
        if (firstLoad || !props.order) return;

        const appointments = props.order.items.appointments ?? [];
        if (latePickupChecked) {
            const calculatedAmount = calculateLateCheckoutFee(appointments, timezone);
            const customLatePickUpFeeCopy = { ...customLatePickUpFee };
            customLatePickUpFeeCopy.orderItem.amount = calculatedAmount.toString();
            setCustomLatePickUpFee(customLatePickUpFeeCopy);
            saveLatePickUpFee(customLatePickUpFeeCopy.orderItem);
            return;
        }

        const automaticAmount = calculateAutomaticLateCheckoutFee(appointments, timezone);
        if (automaticAmount > 0) {
            const orderItem = buildLateCheckoutFeeOrderItem({
                order: props.order,
                amount: automaticAmount,
                timeZone: timezone,
                uuid: customLatePickUpFee.orderItem.uuid,
                existing: findLateCheckoutFeeCustomItem(props.order) ?? customLatePickUpFee
            });
            setCustomLatePickUpFee(prev => ({ ...prev, orderItem }));
            dispatch(saveOrderItemThunk(apiClientWrapper, orderItem));
            return;
        }

        if (customLatePickUpFee.orderItem.id) {
            setCustomLatePickUpFee(prev => ({
                ...prev,
                orderItem: {
                    ...prev.orderItem,
                    id: undefined,
                    amount: '0'
                }
            }));
            removeLatePickUpFee();
        }
    }, [latePickupChecked]);

    useEffect(() => {
        if (userSetSwitchRef.current || latePickupChecked) return;
        if (!props.order || props.order.paid || isReadOnly || props.isRequestedOrder || props.isDeclinedOrder) {
            return;
        }

        const appointments = props.order.items.appointments ?? [];
        const automaticAmount = calculateAutomaticLateCheckoutFee(appointments, timezone);
        if (automaticAmount <= 0) return;

        const existing = findLateCheckoutFeeCustomItem(props.order);
        if (existing && lateCheckoutAmountsMatch(Number(existing.orderItem.amount), automaticAmount)) {
            return;
        }

        const applyKey = `${props.order.id}:${automaticAmount}`;
        if (autoAppliedKeyRef.current === applyKey) return;
        autoAppliedKeyRef.current = applyKey;

        const orderItem = buildLateCheckoutFeeOrderItem({
            order: props.order,
            amount: automaticAmount,
            timeZone: timezone,
            uuid: existing?.orderItem.uuid ?? apiClientWrapper.uuid(),
            existing
        });
        setCustomLatePickUpFee(prev => ({ ...prev, orderItem }));
        dispatch(saveOrderItemThunk(apiClientWrapper, orderItem));
    }, [
        props.order,
        props.order?.paid,
        latePickupChecked,
        isReadOnly,
        props.isRequestedOrder,
        props.isDeclinedOrder,
        timezone
    ]);

    useNonInitialEffect(() => {
        switch (ordersStatus) {
            case OrdersStatus.DeletingOrderItem:
            case OrdersStatus.SavingOrderItem:
                setSaving(true);
                break;
            case OrdersStatus.SavingOrder:
                latePickUpFeeScopedSaveRef.current && setSaving(true);
                break;
            case OrdersStatus.SaveOrderScopeCancelled:
                if (latePickUpFeeScopedSaveRef.current) {
                    latePickUpFeeScopedSaveRef.current = false;
                    resetLatePickUpFeeToSavedOrder();
                }
                setSaving(false);
                break;
            case OrdersStatus.SaveOrderScopeRequired:
                setSaving(false);
                break;
            case OrdersStatus.GetSuccess:
                setSaving(false);
                break;
            case OrdersStatus.SaveOrderItemSuccess:
            case OrdersStatus.SaveOrderSuccess:
            case OrdersStatus.DeleteOrderItemSuccess:
            case OrdersStatus.ReloadSuccess:
                latePickUpFeeScopedSaveRef.current = false;
                const existingCustom = findLateCheckoutFeeCustomItem(props.order);
                if (existingCustom) {
                    setCustomLatePickUpFee(existingCustom);
                    lastSavedValueRef.current = Number(existingCustom.orderItem.amount);
                    lastDispatchedValueRef.current = undefined;
                }
                setSaving(false);
                break;
            case OrdersStatus.Error:
                autoAppliedKeyRef.current = null;
                if (latePickUpFeeScopedSaveRef.current) {
                    latePickUpFeeScopedSaveRef.current = false;
                    resetLatePickUpFeeToSavedOrder();
                }
                setSaving(false);
                break;
            default:
                setSaving(false);
                break;
        }
    }, [ordersStatus]);

    const fetchRooms = async () => {
        const roomsInfo = { searchText: '', page: 1, pageSize: 100 };
        await dispatch(searchThunk(apiClientWrapper, roomsInfo.searchText, roomsInfo.page, roomsInfo.pageSize));
    };

    const getMarketplaceName = () => {
        const marketplace = props.order?.items.appointments[0].appointment.marketplaceId;
        if (marketplace) {
            const marketplaceData = getOrganizationMarketplace(marketplace);
            return marketplaceData?.name || '';
        }
        return '';
    };

    return (
        <Box className={clsx(props.classNames.bodyContainer, props.classNames.horizontalPaddings)}>
            {props.loading.order && (
                <Box className={props.classNames.orderSpinnerContainer}>
                    <Spinner />
                </Box>
            )}
            {!props.loading.order && props.order && organizationMarketplaces.length > 1 && (
                <div className={clsx(props.classNames.marketplaceContainer, styles.marketplaceContainer)}>
                    <FontAwesomeIcon
                        icon={faLocationDot}
                        className={styles.marketplaceIcon}
                    />
                    <Title
                        title={getMarketplaceName() ? `${getMarketplaceName()}` : ''}
                        className={styles.marketplaceTitle}
                    />
                </div>
            )}
            {!props.loading.order && props.order && props.isRequestedOrder && (
                <RequestedDate date={props.order.createdAt} />
            )}
            {!props.loading.order && props.order && (
                <PetParent
                    client={props.order.client}
                    cardOnFileMarketplaceId={props.order.items.appointments[0]?.appointment.marketplaceId}
                    validatePermissions={true}
                    onShowClient={
                        !isReadOnly
                            ? () =>
                                  props.setShowClientView({
                                      clientId: props.order?.client.id
                                  })
                            : undefined
                    }
                    onClickChat={!isReadOnly ? () => props.setShowTwoWays(true) : undefined}
                />
            )}
            {!props.loading.order && props.order && unsignedAgreements.length > 0 && (
                <Box className={styles.unsignedAgreementAlert}>
                    <UnsignedAgreementAlert
                        agreements={unsignedAgreements}
                        clientId={props.order.client.id!}
                        onSigned={() => dispatch(getOrderThunk(apiClientWrapper, props.order!.id))}
                    />
                </Box>
            )}
            {!props.loading.order && props.order && showDepositPendingBanner && (
                <Box className={styles.depositPendingBanner}>
                    <AlertNewBooking
                        className={styles.depositPendingAlert}
                        bWidth="2px"
                        icon={faExclamationTriangle}
                        iconSize="lg"
                        iconColor="#EF4F57"
                        bgColor="#FBB9BC33"
                        bdColor="#EF4F57"
                    >
                        <Typography className={styles.depositPendingText}>
                            Deposit Pending.{' '}
                            {isReadOnly || !props.onOpenCollectDeposit ? (
                                <span className={styles.depositPendingLinkDisabled}>Collect Deposit</span>
                            ) : (
                                <span
                                    className={styles.depositPendingLink}
                                    onClick={() => props.onOpenCollectDeposit?.()}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            props.onOpenCollectDeposit?.();
                                        }
                                    }}
                                >
                                    Collect Deposit
                                </span>
                            )}
                        </Typography>
                    </AlertNewBooking>
                </Box>
            )}
            {!props.loading.order && props.order && props.isRequestedOrder && (
                <Title
                    title="Booking Detail"
                    className={clsx(props.classNames.section, props.classNames.bookingDetailTitle)}
                />
            )}
            {!props.loading.order && props.order && props.isRequestedOrder && (
                <Button
                    color="black"
                    label="View on Calendar"
                    onClick={() => props.goToCalendaryDayHandler(props.order!)}
                    className={props.classNames.viewCalendarButton}
                    disabled={isReadOnly}
                />
            )}
            {!props.loading.order &&
                props.order &&
                props.order?.items.appointments[0] &&
                props.order.items.appointments[0].appointment.recurringUuid && (
                    <Box className={props.classNames.recurrencyContainer}>
                        {!props.loading.recurrent && (
                            <BookingRecurrencyInfo
                                recurrent={props.recurrentInfo}
                                duration={props.order?.items.appointments[0].appointment.duration}
                            />
                        )}
                    </Box>
                )}
            {!props.loading.order && props.order && props.canExtendSeries && (
                <ExtendRecurringSeries
                    extending={props.seriesExtending}
                    disabled={isReadOnly}
                    onExtend={props.extendSeries}
                    zIndex={props.resultsDrawerZIndex}
                />
            )}
            {!props.loading.order &&
                !props.loading.recurrent &&
                props.order &&
                !isOrderInRecurringSeries(props.order) &&
                props.isLastRecurrentAppointment && (
                    <AlertNewBooking
                        bWidth="2px"
                        icon={faInfoCircle}
                        iconSize="lg"
                        iconColor="#BAA997"
                        bgColor="#F8F5F1"
                        bdColor="#BCB8AE"
                        className={props.classNames.lastRecurrentWarning}
                    >
                        <Typography className={props.classNames.lastRecurrentAlertText}>
                            Only the recurring service for one pet will be extended. Any added services or pets
                            won&apos;t be included.
                        </Typography>
                        <Button
                            label="Extend"
                            color="black"
                            onClick={props.extend}
                            className={props.classNames.extendButton}
                            loading={props.loading.extend}
                            disabled={isReadOnly}
                        />
                    </AlertNewBooking>
                )}
            {!props.loading.order &&
                props.order &&
                props.sortedPets.map(pet => {
                    const petIsReadOnly = isReadOnly || !petHasActiveService(pet.id);
                    const petWithoutOptions = props.isRequestedOrder || props.isDeclinedOrder || petIsReadOnly;
                    const petAppointments = (props.order?.items.appointments ?? []).filter(
                        item => item.pet.id === pet.id
                    );
                    const petEditable =
                        !petIsReadOnly &&
                        petAppointments.some(
                            item =>
                                item.appointment.id != null &&
                                props.canEditAppointmentItem(item.appointment.id, item.appointment.status.id)
                        );

                    return (
                        <PetDetail
                            key={pet.id}
                            pet={pet}
                            orderItems={props.orderItemsByPet.get(pet.id!)}
                            onlyOnePet={props.hasOnlyOnePet}
                            editingOrder={props.edit.order}
                            editable={petEditable}
                            readOnly={petIsReadOnly}
                            isEvaluation={props.isEvaluation}
                            withoutOptions={petWithoutOptions}
                            suppressAppointmentOptions={!props.canAddItemsAndPets}
                            canEditAppointmentItem={props.canEditAppointmentItem}
                            disableDeleteService={props.hasOnlyOneService || petIsReadOnly}
                            savingAppointmentId={props.savingStatusAppointmentId}
                            resultsDrawerZIndex={props.resultsDrawerZIndex}
                            className={props.classNames.petDetail}
                            onEdit={!petIsReadOnly ? props.editOrderItemHandler : undefined}
                            onAdd={
                                !petIsReadOnly
                                    ? orderItemType => props.addOrderItemHandler(pet.id || 0, orderItemType)
                                    : undefined
                            }
                            onDelete={!petIsReadOnly ? props.confirmDeleteHanlder : undefined}
                            onViewReport={props.viewReportHandler}
                            onAddReport={!petIsReadOnly ? props.addReportHandler : undefined}
                            onDeletePet={!petIsReadOnly ? () => props.confirmDeletePetHandler(pet) : undefined}
                            onAddEvaluation={
                                !petIsReadOnly
                                    ? () =>
                                          props.onAddEvaluation &&
                                          props.onAddEvaluation(pet.id || 0, OrderizableTypes.APPOINTMENT)
                                    : undefined
                            }
                            onStartSavingStatus={props.setSavingStatusAppointmentId}
                            onStopSavingStatus={() => props.setSavingStatusAppointmentId(undefined)}
                            onRebook={!petIsReadOnly ? props.setServiceToRebook : undefined}
                            onShowPetCard={
                                !petIsReadOnly
                                    ? (optSelected?: string) =>
                                          props.setShowPetCard({
                                              petId: pet.id,
                                              optionSelected: optSelected
                                          })
                                    : undefined
                            }
                            onViewResults={props.onViewResults}
                        />
                    );
                })}
            {!props.loading.order && props.loading.pets && (
                <Box className={clsx(props.classNames.addButtonSpinnerContainer, props.classNames.section)}>
                    <Spinner classNameCircularProgress={props.classNames.addButtonSpinner} />
                </Box>
            )}
            {!props.loading.order &&
                !props.loading.pets &&
                props.canAddItemsAndPets &&
                props.showAddPetButton &&
                !props.isRequestedOrder &&
                !isReadOnly && (
                    <Button
                        color="green"
                        label="+ Add Another Pet"
                        size="small"
                        className={clsx(props.classNames.addAnotherPetButton, props.classNames.section)}
                        disabled={props.savingStatusAppointmentId !== undefined}
                        onClick={() => props.setAddPet(true)}
                    />
                )}
            {!props.loading.order && (
                <Box className={props.classNames.latePickUpFee}>
                    <LatePickUpFeeToggle
                        loading={saving}
                        checked={latePickupChecked}
                        currentValue={Number(customLatePickUpFee.orderItem.amount)}
                        setCurrentValue={handleSetCurrentValue}
                        setChecked={() => {
                            if (isReadOnly) return;
                            userSetSwitchRef.current = true;
                            setLatePickupChecked(!latePickupChecked);
                        }}
                        disabled={isReadOnly}
                    />
                </Box>
            )}
            {!props.loading.order && showDepositToggle && (
                <Box className={styles.depositToggleContainer}>
                    <span className={styles.depositToggleTitle}>Deposit</span>
                    <Box className={styles.depositToggleAction}>
                        {props.depositToggleLoading ? (
                            <Box className={styles.depositToggleSpinnerSlot}>
                                <V3Spinner size={25} />
                            </Box>
                        ) : (
                            <Switch
                                id="order_detail_deposit_switch"
                                className={styles.depositToggleInput}
                                checked={hasDeposit}
                                disabled={depositToggleDisabled}
                                onChange={props.onToggleDeposit}
                            />
                        )}
                    </Box>
                </Box>
            )}
            {!props.loading.order && (
                <Notes
                    notes={props.notes}
                    className={props.classNames.section}
                    onChange={!isReadOnly ? props.setNotes : undefined}
                    disabled={isReadOnly}
                />
            )}
            {!props.loading.order && props.order && <Box className={props.classNames.bodyBottomSpace} />}
        </Box>
    );
};

export default OrderDetailDetailsTab;
